/**
 * Nextrip¹ — Unified traveller feedback
 * Voice players (single-play) + screenshot lightbox + filters
 */
(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var activePlayer = null;

  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  function formatTime(seconds) {
    if (!isFinite(seconds) || seconds < 0) return '0:00';
    var m = Math.floor(seconds / 60);
    var s = Math.floor(seconds % 60);
    return m + ':' + (s < 10 ? '0' : '') + s;
  }

  function setProgress(card, ratio) {
    var pct = Math.max(0, Math.min(1, ratio)) * 100;
    var bar = card.querySelector('[data-n1-voice-bar]');
    var thumb = card.querySelector('[data-n1-voice-thumb]');
    var progress = card.querySelector('[data-n1-voice-progress]');
    if (bar) bar.style.width = pct + '%';
    if (thumb) thumb.style.insetInlineStart = pct + '%';
    if (progress) progress.setAttribute('aria-valuenow', String(Math.round(pct)));
  }

  function setPlayingUI(card, playing) {
    var toggle = card.querySelector('[data-n1-voice-toggle]');
    var playIcon = card.querySelector('.n1-voice-player__icon--play');
    var pauseIcon = card.querySelector('.n1-voice-player__icon--pause');
    var status = card.querySelector('[data-n1-voice-status]');
    var meta = card.querySelector('[data-n1-voice-listen]');
    card.classList.toggle('is-playing', playing);
    if (playIcon) playIcon.hidden = !!playing;
    if (pauseIcon) pauseIcon.hidden = !playing;
    if (toggle) {
      toggle.setAttribute(
        'aria-label',
        playing
          ? toggle.getAttribute('data-pause-label') || 'Pause'
          : toggle.getAttribute('data-play-label') || 'Play'
      );
    }
    if (status && meta) {
      status.textContent = playing
        ? meta.getAttribute('data-n1-voice-playing') || 'Playing'
        : meta.getAttribute('data-n1-voice-listen') || 'Listen';
    }
  }

  function pauseActive(exceptCard) {
    if (!activePlayer) return;
    if (exceptCard && activePlayer.card === exceptCard) return;
    try { activePlayer.audio.pause(); } catch (e) {}
    setPlayingUI(activePlayer.card, false);
    activePlayer = null;
  }

  function initPlayer(card) {
    if (!card || card.dataset.voiceInit === 'true') return;
    card.dataset.voiceInit = 'true';

    var audio = card.querySelector('[data-n1-voice-audio]');
    var toggle = card.querySelector('[data-n1-voice-toggle]');
    var progress = card.querySelector('[data-n1-voice-progress]');
    var currentEl = card.querySelector('[data-n1-voice-current]');
    var durationEl = card.querySelector('[data-n1-voice-duration]');
    if (!audio || !toggle) return;

    function syncDuration() {
      if (durationEl && isFinite(audio.duration)) durationEl.textContent = formatTime(audio.duration);
    }

    audio.addEventListener('loadedmetadata', syncDuration);
    audio.addEventListener('durationchange', syncDuration);
    audio.addEventListener('timeupdate', function () {
      if (!audio.duration) return;
      setProgress(card, audio.currentTime / audio.duration);
      if (currentEl) currentEl.textContent = formatTime(audio.currentTime);
    });
    audio.addEventListener('ended', function () {
      setPlayingUI(card, false);
      setProgress(card, 0);
      if (currentEl) currentEl.textContent = '0:00';
      if (activePlayer && activePlayer.card === card) activePlayer = null;
    });
    audio.addEventListener('pause', function () {
      if (!audio.ended) setPlayingUI(card, false);
      if (activePlayer && activePlayer.card === card && audio.paused) activePlayer = null;
    });
    audio.addEventListener('play', function () {
      pauseActive(card);
      activePlayer = { card: card, audio: audio };
      setPlayingUI(card, true);
    });

    toggle.addEventListener('click', function () {
      if (audio.paused) {
        pauseActive(card);
        var p = audio.play();
        if (p && typeof p.catch === 'function') p.catch(function () { setPlayingUI(card, false); });
      } else {
        audio.pause();
      }
    });

    function seekFromEvent(e) {
      if (!audio.duration || !progress) return;
      var rect = progress.getBoundingClientRect();
      var clientX = e.clientX;
      if (e.touches && e.touches[0]) clientX = e.touches[0].clientX;
      var isRtl = document.body.classList.contains('is-rtl');
      var ratio = isRtl ? (rect.right - clientX) / rect.width : (clientX - rect.left) / rect.width;
      ratio = Math.max(0, Math.min(1, ratio));
      audio.currentTime = ratio * audio.duration;
      setProgress(card, ratio);
      if (currentEl) currentEl.textContent = formatTime(audio.currentTime);
    }

    var seeking = false;
    if (progress) {
      progress.addEventListener('pointerdown', function (e) {
        seeking = true;
        progress.setPointerCapture(e.pointerId);
        seekFromEvent(e);
      });
      progress.addEventListener('pointermove', function (e) {
        if (seeking) seekFromEvent(e);
      });
      progress.addEventListener('pointerup', function () { seeking = false; });
      progress.addEventListener('pointercancel', function () { seeking = false; });
      progress.addEventListener('keydown', function (e) {
        if (!audio.duration) return;
        var step = Math.max(1, audio.duration * 0.05);
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
          e.preventDefault();
          var dir = e.key === 'ArrowRight' ? 1 : -1;
          if (document.body.classList.contains('is-rtl')) dir *= -1;
          audio.currentTime = Math.max(0, Math.min(audio.duration, audio.currentTime + dir * step));
        }
      });
    }
  }

  function initFilters(section) {
    var buttons = section.querySelectorAll('[data-n1-fb-filter]');
    var items = section.querySelectorAll('[data-n1-fb-item]');
    if (!buttons.length) return;

    buttons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var type = btn.getAttribute('data-n1-fb-filter') || 'all';
        buttons.forEach(function (b) {
          var on = b === btn;
          b.classList.toggle('is-active', on);
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        items.forEach(function (item) {
          var itemType = item.getAttribute('data-fb-type');
          var show = type === 'all' || itemType === type;
          item.classList.toggle('is-filtered-out', !show);
        });
      });
    });
  }

  function initLightbox(section) {
    var lightbox = section.querySelector('[data-n1-fb-lightbox]');
    if (!lightbox) return;
    var img = lightbox.querySelector('[data-n1-fb-lightbox-img]');
    var shots = Array.prototype.slice.call(section.querySelectorAll('[data-n1-fb-shot]'));
    var index = 0;
    var touchX = null;

    function openAt(i) {
      if (!shots.length) return;
      index = (i + shots.length) % shots.length;
      var shot = shots[index];
      img.src = shot.getAttribute('data-shot-src') || '';
      img.alt = shot.getAttribute('data-shot-alt') || '';
      lightbox.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      var closeBtn = lightbox.querySelector('.n1-fb-lightbox__close');
      if (closeBtn) closeBtn.focus();
    }

    function close() {
      lightbox.hidden = true;
      img.src = '';
      document.documentElement.style.overflow = '';
    }

    shots.forEach(function (shot, i) {
      shot.addEventListener('click', function () { openAt(i); });
    });

    lightbox.querySelectorAll('[data-n1-fb-lightbox-close]').forEach(function (el) {
      el.addEventListener('click', close);
    });
    var prev = lightbox.querySelector('[data-n1-fb-lightbox-prev]');
    var next = lightbox.querySelector('[data-n1-fb-lightbox-next]');
    if (prev) prev.addEventListener('click', function () { openAt(index - 1); });
    if (next) next.addEventListener('click', function () { openAt(index + 1); });

    document.addEventListener('keydown', function (e) {
      if (lightbox.hidden) return;
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowLeft') openAt(index - 1);
      if (e.key === 'ArrowRight') openAt(index + 1);
    });

    lightbox.addEventListener('touchstart', function (e) {
      if (!e.changedTouches || !e.changedTouches[0]) return;
      touchX = e.changedTouches[0].clientX;
    }, { passive: true });
    lightbox.addEventListener('touchend', function (e) {
      if (touchX == null || !e.changedTouches || !e.changedTouches[0]) return;
      var dx = e.changedTouches[0].clientX - touchX;
      touchX = null;
      if (Math.abs(dx) < 40) return;
      if (dx > 0) openAt(index - 1);
      else openAt(index + 1);
    }, { passive: true });
  }

  function initReveal(section) {
    if (!section.classList.contains('n1-fb--motion') || REDUCED) {
      section.classList.add('is-inview');
      return;
    }
    if (!('IntersectionObserver' in window)) {
      section.classList.add('is-inview');
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        section.classList.add('is-inview');
        io.unobserve(section);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });
    io.observe(section);
  }

  function initSection(section) {
    if (!section || section.dataset.fbInit === 'true') return;
    section.dataset.fbInit = 'true';
    initReveal(section);
    initFilters(section);
    initLightbox(section);
    section.querySelectorAll('[data-n1-voice-card]').forEach(initPlayer);
  }

  function boot(root) {
    (root || document).querySelectorAll('[data-n1-fb]').forEach(initSection);
  }

  ready(function () { boot(document); });

  document.addEventListener('shopify:section:load', function (e) {
    if (!e.target) return;
    e.target.querySelectorAll('[data-n1-fb]').forEach(function (section) {
      section.dataset.fbInit = '';
      section.querySelectorAll('[data-n1-voice-card]').forEach(function (card) {
        card.dataset.voiceInit = '';
      });
    });
    boot(e.target);
  });
})();
